import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
} from '@diamond/shared/types';
import { demagnifyQuantity } from '@diamond/shared/utils';

type VoucherDisplayItem = VoucherEligibilityItem | VoucherGetByCodeResponse;
type MessageRuleProduct = NonNullable<
  NonNullable<VoucherEligibilityItem['message_rule_products']>[number]
>;
type RuleProductEntry = NonNullable<
  NonNullable<MessageRuleProduct['products']>[number]
>;
type IneligibilityReason = NonNullable<
  NonNullable<VoucherEligibilityItem['ineligibility_reasons']>[number]
>;
type MinQuantityReason = IneligibilityReason & {
  product_item_code: number;
  product_id?: string;
  required_quantity?: number;
  current_quantity?: number;
  minimum_order_quantity?: number | null;
  order_quantity_multiple?: number | null;
  stock?: number | null;
  stock_status?: string | null;
};

/**
 * Type guard for the MIN_QUANTITY reason. This reason contains the product metadata
 * that we need in order to render actionable CTAs.
 */
const isMinQuantityReason = (
  reason: IneligibilityReason
): reason is MinQuantityReason =>
  reason.type === 'MIN_QUANTITY' && 'product_item_code' in reason;

/**
 * Demagnify helper that always returns a number or null (never undefined) to simplify math.
 */
const demagnifyOrNull = (value: number | null | undefined): number | null =>
  demagnifyQuantity(value) ?? null;

/**
 * Extract `message_rule_products` from either the eligibility payload or the detailed voucher response.
 * Filters out nullish entries to keep downstream logic simple.
 */
const getMessageRuleProducts = (
  voucher: VoucherDisplayItem
): MessageRuleProduct[] => {
  if (!('message_rule_products' in voucher)) {
    return [];
  }

  return (voucher.message_rule_products ?? []).filter(
    (rule): rule is MessageRuleProduct => Boolean(rule)
  );
};

/**
 * Remove nullish entries from the nested `products` array.
 */
const getRuleProducts = (rule: MessageRuleProduct): RuleProductEntry[] =>
  (rule.products ?? []).filter((product): product is RuleProductEntry =>
    Boolean(product)
  );

/**
 * Build a quick lookup table that maps product item codes to their associated
 * MIN_QUANTITY reasons (product metadata).
 */
const buildReasonMap = (
  voucher: VoucherDisplayItem
): Map<number, MinQuantityReason> => {
  if (!('ineligibility_reasons' in voucher)) {
    return new Map();
  }

  const reasons = voucher.ineligibility_reasons ?? [];
  const map = new Map<number, MinQuantityReason>();

  reasons.forEach((reason) => {
    if (reason && isMinQuantityReason(reason)) {
      map.set(reason.product_item_code, reason);
    }
  });

  return map;
};

type QuantitySnapshot = Pick<
  VoucherRuleProductAction,
  | 'requiredQuantity'
  | 'currentQuantity'
  | 'missingQuantity'
  | 'minOrderQuantity'
  | 'orderQuantityMultiple'
  | 'stockQuantity'
  | 'stockStatus'
  | 'normalizedMissingQuantity'
  | 'isOutOfStock'
>;

const MAX_DECIMAL_PLACES = 3;

type AlignQuantityParams = {
  candidate: number;
  minOrderQuantity: number | null;
  orderQuantityMultiple: number | null;
  stockQuantity: number | null;
};

const getPrecision = (value: number | null): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }

  const decimalPart = value.toString().split('.')[1];
  return Math.min(decimalPart?.length ?? 0, MAX_DECIMAL_PLACES);
};

const alignQuantityToRules = ({
  candidate,
  minOrderQuantity,
  orderQuantityMultiple,
  stockQuantity,
}: AlignQuantityParams): number => {
  let normalized = candidate;

  if (typeof minOrderQuantity === 'number' && minOrderQuantity > 0) {
    normalized = Math.max(normalized, minOrderQuantity);
  }

  if (typeof orderQuantityMultiple === 'number' && orderQuantityMultiple > 0) {
    const multiples = Math.max(
      1,
      Math.ceil(normalized / orderQuantityMultiple)
    );
    normalized = multiples * orderQuantityMultiple;
  }

  if (typeof stockQuantity === 'number') {
    normalized = Math.min(normalized, Math.max(stockQuantity, 0));
  }

  if (normalized <= 0) {
    return Math.max(minOrderQuantity ?? 0, orderQuantityMultiple ?? 0, 0);
  }

  const precision = Math.max(
    getPrecision(orderQuantityMultiple),
    getPrecision(minOrderQuantity),
    getPrecision(normalized)
  );

  return Number(normalized.toFixed(precision));
};

const detectOutOfStock = (
  stockQuantity: number | null,
  stockStatus?: string
): boolean => {
  if (typeof stockQuantity === 'number') {
    return stockQuantity <= 0;
  }

  return stockStatus === 'OUT_OF_STOCK';
};

/**
 * Convert raw quantities (which may still be magnified) into UI-friendly numbers.
 * Returns the data points needed for CTA defaults and analytics.
 */
const resolveQuantities = (
  reason: MinQuantityReason,
  ruleProduct: RuleProductEntry
): QuantitySnapshot => {
  const requiredQuantity =
    demagnifyOrNull(reason.required_quantity ?? ruleProduct.min_quantity) ?? 0;
  const currentQuantity = demagnifyOrNull(reason.current_quantity) ?? 0;
  const minOrderQuantity = demagnifyOrNull(reason.minimum_order_quantity);
  const orderQuantityMultiple = demagnifyOrNull(reason.order_quantity_multiple);
  const fallbackMissing =
    demagnifyOrNull(ruleProduct.min_quantity) ??
    minOrderQuantity ??
    requiredQuantity;
  const missingQuantityCandidate = Math.max(
    requiredQuantity - currentQuantity,
    0
  );
  const missingQuantity =
    missingQuantityCandidate > 0
      ? missingQuantityCandidate
      : Math.max(fallbackMissing ?? 0, minOrderQuantity ?? 0);

  const stockQuantity = demagnifyOrNull(reason.stock);
  const stockStatus =
    'stock_status' in reason && typeof reason.stock_status === 'string'
      ? reason.stock_status
      : undefined;

  const normalizedMissingQuantity = alignQuantityToRules({
    candidate: missingQuantity,
    minOrderQuantity,
    orderQuantityMultiple,
    stockQuantity,
  });

  const isOutOfStock = detectOutOfStock(stockQuantity, stockStatus);

  return {
    requiredQuantity,
    currentQuantity,
    missingQuantity,
    minOrderQuantity,
    orderQuantityMultiple,
    stockQuantity,
    stockStatus,
    normalizedMissingQuantity,
    isOutOfStock,
  };
};

export type VoucherRuleProductAction = {
  /** Stable id used for rendering / analytics */
  id: string;
  analyticCode: string;
  ruleIndex: number;
  message: string;
  voucherCode: string;
  voucherId?: string;
  isBundling: boolean;
  productId: string;
  productItemCode: number;
  productName: string;
  requiredQuantity: number;
  currentQuantity: number;
  missingQuantity: number;
  minOrderQuantity: number | null;
  orderQuantityMultiple: number | null;
  normalizedMissingQuantity: number;
  stockQuantity: number | null;
  stockStatus?: string;
  isOutOfStock: boolean;
};

export type VoucherRuleProductGroup = {
  id: string;
  message: string;
  ruleIndex: number;
  isBundling: boolean;
  products: VoucherRuleProductAction[];
};

/**
 * Convert voucher rule messages into actionable product-level entries.
 *
 * Input: Voucher card data (`message_rule_products`) and `ineligibility_reasons`.
 * Output: Groups of rule products, each containing the product metadata and the
 *         quantity that the shopper still needs to add.
 *
 * The returned structure powers the "Tambah produk" CTA list in both the voucher
 * list and the voucher detail view.
 */
export const mapVoucherRuleProducts = (
  voucher: VoucherDisplayItem
): VoucherRuleProductGroup[] => {
  const messageRuleProducts = getMessageRuleProducts(voucher);
  if (!messageRuleProducts.length) {
    return [];
  }

  const isBundling =
    'reward' in voucher ? Boolean(voucher.reward?.is_bundling) : false;
  const reasonMap = buildReasonMap(voucher);

  const groups: VoucherRuleProductGroup[] = [];
  const seenActionIds = new Set<string>();

  messageRuleProducts.forEach((rule, ruleIndex) => {
    const products: VoucherRuleProductAction[] = [];

    getRuleProducts(rule).forEach((ruleProduct) => {
      const reason = reasonMap.get(ruleProduct.product_item_code);
      if (
        !reason ||
        !('product_id' in reason) ||
        typeof reason.product_id !== 'string'
      ) {
        return;
      }

      const actionId = `${voucher.code}-${ruleProduct.product_item_code}`;
      if (seenActionIds.has(actionId)) {
        return;
      }

      seenActionIds.add(actionId);
      const quantities = resolveQuantities(reason, ruleProduct);

      products.push({
        id: actionId,
        analyticCode: actionId,
        ruleIndex,
        message: rule.message,
        voucherCode: voucher.code,
        voucherId: 'id' in voucher ? voucher.id : undefined,
        isBundling,
        productId: reason.product_id,
        productItemCode: ruleProduct.product_item_code,
        productName: ruleProduct.product_name,
        ...quantities,
      });
    });

    if (products.length > 0) {
      groups.push({
        id: `${voucher.code}-rule-${ruleIndex}`,
        message: rule.message,
        ruleIndex,
        isBundling,
        products,
      });
    }
  });

  return groups;
};
